import { useState, useEffect, useCallback, useRef } from 'react';
import { Check, ArrowRight, Eye, EyeOff, Sparkles, Zap, Brain, Heart, Target, Trophy } from 'lucide-react';

// --- Text Block ---
export function TextBlock({ content, onComplete }) {
  return (
    <div className="flex flex-col items-center justify-center text-center max-w-2xl mx-auto animate-fade-in" data-testid="block-text">
      <h2
        className="text-3xl md:text-4xl font-bold tracking-tight mb-6"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        {content.title}
      </h2>
      <p className="text-lg text-zinc-600 leading-relaxed mb-10">
        {content.body}
      </p>
      <button
        onClick={onComplete}
        className="btn-primary flex items-center gap-3"
        data-testid="text-continue-btn"
      >
        Continue <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// --- Word Display Block ---
export function WordDisplayBlock({ content, onComplete }) {
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [showInstruction, setShowInstruction] = useState(true);
  const words = content.words || [];
  const displayTime = (content.display_time || 4) * 1000;
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    if (showInstruction) return;
    if (currentIndex >= words.length) {
      onCompleteRef.current?.();
      return;
    }
    const timer = setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
    }, currentIndex === -1 ? 500 : displayTime);
    return () => clearTimeout(timer);
  }, [currentIndex, showInstruction, words.length, displayTime]);

  if (showInstruction) {
    return (
      <div className="flex flex-col items-center justify-center text-center animate-fade-in" data-testid="block-word-display-intro">
        <Eye className="w-8 h-8 text-[#09090B] mb-6" />
        <p className="text-lg text-zinc-600 mb-8 max-w-lg">{content.instruction}</p>
        <p className="mono-data text-sm text-zinc-400 mb-4">{words.length} words &middot; {content.display_time}s each</p>
        <button
          onClick={() => { setShowInstruction(false); setCurrentIndex(0); }}
          className="btn-accent flex items-center gap-3"
          data-testid="word-display-start-btn"
        >
          Show Words <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (currentIndex >= words.length) return null;

  return (
    <div className="flex flex-col items-center justify-center text-center animate-fade-in" data-testid="block-word-display">
      <p className="mono-data text-xs text-zinc-400 mb-8">
        Word {currentIndex + 1} of {words.length}
      </p>
      <div
        className="word-display mb-8"
        key={currentIndex}
        style={{ animation: 'fadeIn 0.3s ease-out' }}
        data-testid={`word-${currentIndex}`}
      >
        {words[currentIndex]}
      </div>
      <div className="w-48 h-1 bg-zinc-200">
        <div
          className="h-full bg-[#09090B] transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / words.length) * 100}%` }}
        />
      </div>
    </div>
  );
}

// --- Recall Input Block (legacy: typed-only) ---
// Kept as an internal component. Public export is `SmartRecallBlock` which
// auto-picks the input mode by user age (MCQ for ≤9, hint-typing for 10-12,
// typing-with-hint for 13-15, typing-only for 16+). Legacy `RecallInputBlock`
// alias still exported below for backward compat with any caller that doesn't
// pass age.
function RecallTypedInput({ content, onComplete, onScore, allowFirstLetterHint = false }) {
  const [input, setInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const targetWords = content.target_words || [];

  const handleSubmit = () => {
    const userWords = input
      .split(/[,\n]+/)
      .map(w => w.trim().toLowerCase())
      .filter(Boolean);

    const targets = targetWords.map(w => w.toLowerCase());
    const correct = userWords.filter(w => targets.includes(w));
    const accuracy = targets.length > 0 ? (correct.length / targets.length) * 100 : 0;

    const scoreData = {
      correct: correct.length,
      total: targets.length,
      accuracy: Math.round(accuracy),
      words_recalled: userWords,
      correct_words: correct
    };

    setResult(scoreData);
    setSubmitted(true);
    if (onScore) onScore(scoreData);
  };

  if (submitted && result) {
    return (
      <div className="flex flex-col items-center justify-center text-center max-w-xl mx-auto animate-fade-in" data-testid="block-recall-result">
        <h3
          className="text-2xl font-bold tracking-tight mb-4"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          You remembered {result.correct} out of {result.total}
        </h3>
        <div className="mono-data text-4xl font-black text-[#09090B] mb-6">
          {result.accuracy}%
        </div>
        <div className="flex flex-wrap gap-2 mb-8">
          {targetWords.map(word => (
            <span
              key={word}
              className={`px-3 py-1 border text-sm ${
                result.correct_words.includes(word.toLowerCase())
                  ? 'border-[#09090B] text-[#09090B] bg-zinc-100'
                  : 'border-zinc-300 text-zinc-400'
              }`}
            >
              {word}
            </span>
          ))}
        </div>
        <button onClick={onComplete} className="btn-primary flex items-center gap-3" data-testid="recall-continue-btn">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center max-w-xl mx-auto animate-fade-in" data-testid="block-recall-input">
      <p className="text-lg text-zinc-600 text-center mb-8">{content.instruction}</p>
      {allowFirstLetterHint && (
        <div className="w-full mb-3 flex flex-wrap gap-2 justify-center">
          {targetWords.map(w => (
            <span key={w} className="mono-data text-xs text-zinc-500 px-2 py-1 border border-zinc-200">
              {w[0].toUpperCase()}{'_'.repeat(Math.max(0, w.length - 1))}
            </span>
          ))}
        </div>
      )}
      <textarea
        value={input}
        onChange={e => setInput(e.target.value)}
        placeholder="Type words separated by commas..."
        className="input-swiss text-base h-32 resize-none border-2 border-[#09090B] p-4 w-full focus:border-[#09090B]"
        data-testid="recall-input-field"
      />
      <div className="flex items-center justify-between w-full mt-4">
        <span className="mono-data text-xs text-zinc-400">
          {content.time_limit ? `${content.time_limit}s limit` : ''}
          {allowFirstLetterHint && targetWords.length > 0 ? ' · first-letter hints shown' : ''}
        </span>
        <button
          onClick={handleSubmit}
          disabled={!input.trim()}
          className="btn-accent flex items-center gap-3 disabled:opacity-40"
          data-testid="recall-submit-btn"
        >
          <Check className="w-4 h-4" /> Submit
        </button>
      </div>
    </div>
  );
}

// --- Recall MCQ Block (tap-to-select, kid-friendly) ---
function RecallMCQ({ content, onComplete, onScore }) {
  const targetWords = content.target_words || [];
  const distractors = content.distractors || [];
  const [picked, setPicked] = useState(new Set());
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  // Stable shuffled tile order — only computed once per mount
  const tiles = useRef(null);
  if (!tiles.current) {
    const all = [...targetWords, ...distractors];
    // Fisher-Yates
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }
    tiles.current = all;
  }

  const togglePick = (word) => {
    if (submitted) return;
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(word)) next.delete(word); else next.add(word);
      return next;
    });
  };

  const handleSubmit = () => {
    const targets = targetWords.map(w => w.toLowerCase());
    const userWords = Array.from(picked).map(w => w.toLowerCase());
    const correct = userWords.filter(w => targets.includes(w));
    const wrong_picks = userWords.filter(w => !targets.includes(w));
    const accuracy = targets.length > 0
      ? Math.max(0, Math.round((correct.length - wrong_picks.length * 0.5) / targets.length * 100))
      : 0;
    const scoreData = {
      correct: correct.length,
      total: targets.length,
      accuracy,
      words_recalled: userWords,
      correct_words: correct,
    };
    setResult(scoreData);
    setSubmitted(true);
    if (onScore) onScore(scoreData);
  };

  if (submitted && result) {
    return (
      <div className="flex flex-col items-center justify-center text-center max-w-xl mx-auto animate-fade-in" data-testid="block-recall-mcq-result">
        <h3 className="text-2xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          You got {result.correct} out of {result.total}
        </h3>
        <div className="mono-data text-4xl font-black text-[#09090B] mb-6">{result.accuracy}%</div>
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {targetWords.map(word => (
            <span key={word}
              className={`px-3 py-1 border text-sm ${result.correct_words.includes(word.toLowerCase())
                ? 'border-[#09090B] text-[#09090B] bg-zinc-100'
                : 'border-zinc-300 text-zinc-400'}`}>
              {word}
            </span>
          ))}
        </div>
        <button onClick={onComplete} className="btn-primary flex items-center gap-3" data-testid="recall-continue-btn">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center max-w-2xl mx-auto animate-fade-in" data-testid="block-recall-mcq">
      <p className="text-lg text-zinc-600 text-center mb-2">{content.instruction}</p>
      <p className="mono-data text-xs text-zinc-400 mb-6">Tap each word you remember</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 w-full mb-6">
        {tiles.current.map(word => {
          const isPicked = picked.has(word);
          return (
            <button
              key={word}
              onClick={() => togglePick(word)}
              className={`px-4 py-5 text-base font-medium border-2 transition-all ${isPicked
                ? 'border-[#09090B] bg-[#09090B] text-white'
                : 'border-[#09090B] bg-white text-[#09090B] hover:bg-zinc-100'}`}
              data-testid={`mcq-tile-${word.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {word}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between w-full">
        <span className="mono-data text-xs text-zinc-400">{picked.size} selected</span>
        <button
          onClick={handleSubmit}
          disabled={picked.size === 0}
          className="btn-accent flex items-center gap-3 disabled:opacity-40"
          data-testid="recall-submit-btn"
        >
          <Check className="w-4 h-4" /> Submit
        </button>
      </div>
    </div>
  );
}

// --- Smart Recall Block (auto-picks mode by user age) ---
// modes: 'mcq' | 'hint' | 'typing'
// User age is read from the AuthContext via the user prop passed by SessionPlayer.
export function RecallInputBlock({ content, onComplete, onScore, userAge }) {
  // Allow content-level override (e.g. force 'typing' for an adult-only premium drill)
  const explicitMode = content.mode;
  const age = typeof userAge === 'number' ? userAge : null;

  let mode = explicitMode;
  if (!mode) {
    if (age === null) mode = 'typing';
    else if (age <= 9) mode = 'mcq';
    else if (age <= 12) mode = 'mcq';
    else if (age <= 15) mode = 'hint';
    else mode = 'typing';
  }

  // MCQ requires distractors. If absent, fall back to hint-typing.
  if (mode === 'mcq' && !(content.distractors && content.distractors.length > 0)) {
    mode = 'hint';
  }

  if (mode === 'mcq') {
    return <RecallMCQ content={content} onComplete={onComplete} onScore={onScore} />;
  }
  return (
    <RecallTypedInput
      content={content}
      onComplete={onComplete}
      onScore={onScore}
      allowFirstLetterHint={mode === 'hint'}
    />
  );
}

// --- Story Builder Block ---
export function StoryBuilderBlock({ content, onComplete }) {
  const [story, setStory] = useState('');

  return (
    <div className="flex flex-col items-center justify-center max-w-2xl mx-auto animate-fade-in" data-testid="block-story-builder">
      <p className="text-lg text-zinc-600 text-center mb-4">{content.instruction}</p>
      {content.words && (
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          {content.words.map(word => (
            <span key={word} className="px-3 py-1 border border-[#09090B] text-[#09090B] text-sm font-medium">
              {word}
            </span>
          ))}
        </div>
      )}
      <textarea
        value={story}
        onChange={e => setStory(e.target.value)}
        placeholder={content.prompt || 'Write your story here...'}
        className="input-swiss text-base h-40 resize-none border-2 border-[#09090B] p-4 w-full focus:border-[#09090B]"
        data-testid="story-input-field"
      />
      <button
        onClick={onComplete}
        disabled={!story.trim()}
        className="btn-primary flex items-center gap-3 mt-6 disabled:opacity-40"
        data-testid="story-continue-btn"
      >
        Continue <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// --- Visualization Block ---
export function VisualizationBlock({ content, onComplete }) {
  const [timeLeft, setTimeLeft] = useState(content.duration || 60);
  const [started, setStarted] = useState(false);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    if (!started) return;
    if (timeLeft <= 0) {
      onCompleteRef.current?.();
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [started, timeLeft]);

  if (!started) {
    return (
      <div className="flex flex-col items-center justify-center text-center max-w-lg mx-auto animate-fade-in" data-testid="block-visualization-intro">
        <EyeOff className="w-8 h-8 text-[#09090B] mb-6" />
        <p className="text-lg text-zinc-600 leading-relaxed mb-8">{content.instruction}</p>
        <button onClick={() => setStarted(true)} className="btn-accent flex items-center gap-3" data-testid="visualization-start-btn">
          Begin Visualization
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center text-center animate-fade-in" data-testid="block-visualization">
      <p className="text-lg text-zinc-600 mb-8 max-w-lg">{content.instruction}</p>
      <div className="mono-data text-6xl font-black text-[#09090B] mb-8" data-testid="visualization-timer">
        {timeLeft}s
      </div>
      <div className="w-48 h-1 bg-zinc-200 mb-8">
        <div
          className="h-full bg-[#09090B] transition-all duration-1000"
          style={{ width: `${((content.duration - timeLeft) / content.duration) * 100}%` }}
        />
      </div>
      <button onClick={onComplete} className="btn-secondary text-xs px-6 py-2" data-testid="visualization-skip-btn">
        {timeLeft <= 0 ? 'Continue' : "I'm Done"}
      </button>
    </div>
  );
}

// --- Memory Challenge Block ---
export function MemoryChallengeBlock({ content, onComplete, onScore }) {
  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const items = content.items || [];
  const correctOrder = content.correct_order || [];

  const handleSelect = (item) => {
    if (submitted) return;
    if (selected.includes(item)) {
      setSelected(selected.filter(s => s !== item));
    } else {
      setSelected([...selected, item]);
    }
  };

  const handleSubmit = () => {
    let correct = 0;
    selected.forEach((item, i) => {
      if (i < correctOrder.length && item === correctOrder[i]) correct++;
    });
    const accuracy = correctOrder.length > 0 ? (correct / correctOrder.length) * 100 : 0;
    if (onScore) onScore({ correct, total: correctOrder.length, accuracy: Math.round(accuracy) });
    setSubmitted(true);
  };

  return (
    <div className="flex flex-col items-center justify-center max-w-xl mx-auto animate-fade-in" data-testid="block-memory-challenge">
      <p className="text-lg text-zinc-600 text-center mb-4">{content.instruction}</p>

      {/* Selected order */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          {selected.map((item, i) => (
            <span
              key={`sel-${i}`}
              className={`px-4 py-2 border text-sm font-medium ${
                submitted
                  ? i < correctOrder.length && item === correctOrder[i]
                    ? 'border-[#09090B] text-[#09090B] bg-zinc-100'
                    : 'border-zinc-700 text-[#09090B] bg-zinc-100'
                  : 'border-[#09090B] text-[#09090B]'
              }`}
            >
              {i + 1}. {item}
            </span>
          ))}
        </div>
      )}

      {/* Items to select */}
      {!submitted && (
        <div className="flex flex-wrap gap-3 mb-8 justify-center">
          {items.map(item => (
            <button
              key={item}
              onClick={() => handleSelect(item)}
              className={`px-6 py-3 border text-sm font-medium transition-colors ${
                selected.includes(item)
                  ? 'border-[#09090B] bg-[#09090B] text-white'
                  : 'border-[#09090B] hover:bg-zinc-100'
              }`}
              data-testid={`challenge-item-${item}`}
            >
              {item}
            </button>
          ))}
        </div>
      )}

      {!submitted ? (
        <button
          onClick={handleSubmit}
          disabled={selected.length !== correctOrder.length}
          className="btn-accent flex items-center gap-3 disabled:opacity-40"
          data-testid="challenge-submit-btn"
        >
          <Check className="w-4 h-4" /> Check Order
        </button>
      ) : (
        <button onClick={onComplete} className="btn-primary flex items-center gap-3" data-testid="challenge-continue-btn">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// --- Reflection Block ---
export function ReflectionBlock({ content, onComplete }) {
  const [text, setText] = useState('');

  return (
    <div className="flex flex-col items-center justify-center max-w-2xl mx-auto animate-fade-in" data-testid="block-reflection">
      <p className="text-lg text-zinc-600 text-center mb-6">{content.instruction}</p>
      {content.prompts && (
        <ul className="text-sm text-zinc-500 mb-6 space-y-2 text-left w-full max-w-md">
          {content.prompts.map((p, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mono-data text-[#09090B]">{String(i + 1).padStart(2, '0')}</span>
              {p}
            </li>
          ))}
        </ul>
      )}
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Write your reflections..."
        className="input-swiss text-base h-40 resize-none border-2 border-[#09090B] p-4 w-full focus:border-[#09090B]"
        data-testid="reflection-input-field"
      />
      <button
        onClick={onComplete}
        disabled={!text.trim()}
        className="btn-primary flex items-center gap-3 mt-6 disabled:opacity-40"
        data-testid="reflection-continue-btn"
      >
        Continue <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// --- Result Block ---
export function ResultBlock({ content, scores, onComplete, isFinal }) {
  const totalCorrect = scores.reduce((sum, s) => sum + (s.correct || 0), 0);
  const totalItems = scores.reduce((sum, s) => sum + (s.total || 0), 0);
  const avgAccuracy = scores.length > 0
    ? Math.round(scores.reduce((sum, s) => sum + (s.accuracy || 0), 0) / scores.length)
    : 0;

  return (
    <div className="flex flex-col items-center justify-center text-center max-w-lg mx-auto animate-fade-in" data-testid="block-result">
      <h2
        className="text-3xl md:text-4xl font-bold tracking-tight mb-2"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        {content.title}
      </h2>
      <p className="text-zinc-600 mb-8">{content.message}</p>

      <div className="grid grid-cols-2 gap-0 border border-[#09090B] w-full mb-8">
        <div className="p-6 border-r border-b border-[#09090B]">
          <div className="label-swiss mb-1">Items Recalled</div>
          <div className="text-3xl font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {totalCorrect}/{totalItems}
          </div>
        </div>
        <div className="p-6 border-b border-[#09090B]">
          <div className="label-swiss mb-1">Accuracy</div>
          <div className="text-3xl font-black tracking-tighter text-[#09090B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {avgAccuracy}%
          </div>
        </div>
        <div className="p-6 border-r border-[#09090B]">
          <div className="label-swiss mb-1">Challenges</div>
          <div className="text-3xl font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {scores.length}
          </div>
        </div>
        <div className="p-6">
          <div className="label-swiss mb-1">Status</div>
          <div className="text-lg font-bold tracking-tight text-[#09090B]">
            {avgAccuracy >= 80 ? 'Excellent' : avgAccuracy >= 50 ? 'Good Progress' : 'Keep Practicing'}
          </div>
        </div>
      </div>

      <button
        onClick={onComplete}
        className={`${isFinal ? 'btn-accent' : 'btn-primary'} flex items-center gap-3`}
        data-testid="result-continue-btn"
      >
        {isFinal ? 'Complete Session' : 'Continue'} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}


// --- Segment Intro Block (the "WHY" before each phase) ---
// Bookends the start of every phase with: hook → science → what we'll do.
// Visually distinct: full-bleed dark card so it reads as a chapter break.
const INTRO_ICONS = { brain: Brain, zap: Zap, target: Target, heart: Heart, sparkles: Sparkles, trophy: Trophy };
export function SegmentIntroBlock({ content, onComplete }) {
  const Icon = INTRO_ICONS[content.icon] || Sparkles;
  return (
    <div className="w-full max-w-3xl mx-auto animate-fade-in" data-testid="block-segment-intro">
      <div className="bg-[#09090B] text-white p-8 md:p-12 border border-[#09090B]">
        <div className="flex items-center gap-3 mb-6">
          <Icon className="w-5 h-5" />
          <p className="mono-data text-xs text-zinc-400">{content.eyebrow || 'Up Next'}</p>
        </div>
        <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-[1.05] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {content.title}
        </h2>
        {content.hook && (
          <p className="text-base md:text-lg text-zinc-200 leading-relaxed mb-6 italic">
            "{content.hook}"
          </p>
        )}
        {content.why && (
          <div className="border-l-2 border-white pl-4 mb-6">
            <p className="mono-data text-[10px] text-zinc-400 mb-1">Why this works</p>
            <p className="text-sm text-zinc-300 leading-relaxed">{content.why}</p>
          </div>
        )}
        {content.what_youll_do && (
          <div>
            <p className="mono-data text-[10px] text-zinc-400 mb-2">What we'll do</p>
            <p className="text-sm text-zinc-300 leading-relaxed">{content.what_youll_do}</p>
          </div>
        )}
      </div>
      <div className="mt-6 flex justify-end">
        <button onClick={onComplete} className="btn-primary flex items-center gap-3" data-testid="segment-intro-continue-btn">
          Let's go <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// --- Segment Summary Block (the "what just happened" after each phase) ---
// Bookends the end of every phase with: brain explanation → forward connection → real-world hook.
export function SegmentSummaryBlock({ content, onComplete }) {
  return (
    <div className="w-full max-w-3xl mx-auto animate-fade-in" data-testid="block-segment-summary">
      <div className="border border-[#09090B] p-8 md:p-12">
        <p className="mono-data text-xs text-zinc-500 mb-4">{content.eyebrow || 'Segment complete'}</p>
        <h2 className="text-2xl md:text-3xl font-black tracking-tighter mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {content.title}
        </h2>

        {content.brain_did && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-4 h-4 text-[#09090B]" />
              <p className="mono-data text-[10px] text-zinc-500">In your brain just now</p>
            </div>
            <p className="text-sm md:text-base text-zinc-700 leading-relaxed">{content.brain_did}</p>
          </div>
        )}

        {content.real_world && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-[#09090B]" />
              <p className="mono-data text-[10px] text-zinc-500">Where you'll feel this</p>
            </div>
            <p className="text-sm md:text-base text-zinc-700 leading-relaxed">{content.real_world}</p>
          </div>
        )}

        {content.next_up && (
          <div className="bg-zinc-100 border-l-2 border-[#09090B] p-4 mt-8">
            <p className="mono-data text-[10px] text-zinc-500 mb-1">Coming up</p>
            <p className="text-sm text-zinc-800 leading-relaxed font-medium">{content.next_up}</p>
          </div>
        )}
      </div>
      <div className="mt-6 flex justify-end">
        <button onClick={onComplete} className="btn-primary flex items-center gap-3" data-testid="segment-summary-continue-btn">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
